import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Select, InputNumber, Row, Col, message, DatePicker } from 'antd';
import dayjs from 'dayjs';
import * as adminactions from 'store/actions/AuthActions';

const { Option } = Select;
const { TextArea } = Input;

const OtherPaymentModal = ({ visible, onCancel, onSuccess, employeeId, editingRecord, dateOfJoining }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [paymentTypes, setPaymentTypes] = useState([]);
    const [typesLoading, setTypesLoading] = useState(false);

    // Disable dates before joining date
    const disabledDate = (current) => {
        if (!dateOfJoining) return false;
        return current && current < dayjs(dateOfJoining).startOf('day');
    };

    // Fetch payment types from API
    useEffect(() => {
        const fetchTypes = async () => {
            setTypesLoading(true);
            const response = await adminactions.fetchPaymentTypes();
            if (response) {
                // Handle both array and paginated response formats
                if (Array.isArray(response)) {
                    setPaymentTypes(response);
                } else if (response.results) {
                    setPaymentTypes(response.results);
                }
            }
            setTypesLoading(false);
        };
        fetchTypes();
    }, []);

    useEffect(() => {
        if (visible) {
            if (editingRecord) {
                form.setFieldsValue({
                    ...editingRecord,
                    payment_type_fk_id: editingRecord.payment_type_fk?.id || editingRecord.payment_type_fk_id,
                    effective_date: editingRecord.effective_date ? dayjs(editingRecord.effective_date) : null
                });
            } else {
                form.resetFields();
            }
        }
    }, [visible, editingRecord, form]);

    const handleSubmit = async (values) => {
        setLoading(true);
        try {
            const data = {
                employee: employeeId,
                effective_date: values.effective_date.format('YYYY-MM-DD'),
                payment_type_fk_id: values.payment_type_fk_id,
                title: values.title,
                amount: values.amount,
                notes: values.notes || null
            };

            if (editingRecord) {
                await adminactions.updateOtherPayment(editingRecord.id, data);
                message.success('Payment updated successfully');
            } else {
                await adminactions.createOtherPayment(data);
                message.success('Payment created successfully');
            }

            onSuccess();
            form.resetFields();
        } catch (error) {
            message.error('Failed to save payment');
            console.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title={editingRecord ? 'Edit Other Payment' : 'Add Other Payment'}
            open={visible}
            onCancel={onCancel}
            onOk={() => form.submit()}
            confirmLoading={loading}
            width={600}
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={{
                    payment_type: 'Other'
                }}
            >
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="effective_date"
                            label="Effective Date"
                            rules={[{ required: true, message: 'Please select effective date' }]}
                        >
                            <DatePicker style={{ width: '100%' }} disabledDate={disabledDate} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="payment_type_fk_id"
                            label="Payment Type"
                            rules={[{ required: true, message: 'Please select payment type' }]}
                        >
                            <Select placeholder="Select type" loading={typesLoading}>
                                {paymentTypes.map(type => (
                                    <Option key={type.id} value={type.id}>
                                        {type.name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item
                            name="title"
                            label="Title"
                            rules={[{ required: true, message: 'Please enter title' }]}
                        >
                            <Input placeholder="e.g., Annual Bonus, Diwali Bonus" />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item
                            name="amount"
                            label="Amount"
                            rules={[{ required: true, message: 'Please enter amount' }]}
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                min={0}
                                step={100}
                                prefix="₹"
                                placeholder="Enter amount"
                            />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item
                            name="notes"
                            label="Notes"
                        >
                            <TextArea rows={3} placeholder="Enter any notes" />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
};

export default OtherPaymentModal;
